/**
 * The invoice page's two pieces of "one check, many jobs" (#1916, design
 * step 1 + step 6). Separate components so their hooks never sit under the
 * invoice page's early returns.
 */
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { centsOf, money, shortDay } from "./payment-copy";
import { recordPaymentHref, useRecordMany } from "./use-record-many";

/** Next to Mark Paid: opens Record a payment with this invoice already ticked. */
export function PartOfBiggerCheckButton({
    invoiceId,
    customerId,
    status,
}: {
    invoiceId: string;
    customerId: string | null | undefined;
    status: string;
}) {
    const on = useRecordMany();
    const navigate = useNavigate();
    if (!on || !customerId || status === "paid" || status === "void") return null;
    return (
        <Button
            size="sm"
            variant="outline"
            data-testid="part-of-bigger-check"
            title="One check paid this and other invoices? Record it once for all of them."
            onClick={() => navigate(recordPaymentHref(customerId, [invoiceId]))}
        >
            <Layers className="h-4 w-4 mr-1" /> Part of a bigger check?
        </Button>
    );
}

/** "✓ Paid by Cintas check #2008419152 · Oct 16 · 1 of 15 · view the check" */
export function ReceivedPaymentTrace({ invoiceId }: { invoiceId: string }) {
    const q = useQuery({
        queryKey: ["invoice-received-payments", invoiceId],
        queryFn: () =>
            api.get<{
                payments: Array<{
                    id: string;
                    method: string;
                    method_label: string;
                    reference: string | null;
                    received_on: string;
                    amount: number;
                    line_amount: number;
                    invoice_count: number;
                    status: string;
                    payer_name: string | null;
                    short_label: string | null;
                }>;
            }>(`/api/invoices/${invoiceId}/received-payments`),
    });
    const rows = (q.data?.payments ?? []).filter((p) => p.status === "saved");
    if (rows.length === 0) return null;
    return (
        <div className="space-y-2 mb-3" data-testid="received-payment-trace">
            {rows.map((p) => (
                <div
                    key={p.id}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-success/40 bg-card px-3 py-2 text-sm font-semibold text-success"
                >
                    <CheckCircle2 className="h-4 w-4" />
                    <span>
                        Paid by {p.payer_name}{" "}
                        {p.method === "check" && p.reference
                            ? `check #${p.reference}`
                            : `${p.method_label}${p.reference ? ` ${p.reference}` : ""}`}{" "}
                        · {shortDay(p.received_on)} · {money(centsOf(p.line_amount))} of {money(centsOf(p.amount))} · 1
                        of {p.invoice_count}
                        {p.short_label ? ` · short: ${p.short_label}` : ""}
                    </span>
                    <Link to={`/money/payments/${p.id}`} className="underline">
                        view the check
                    </Link>
                </div>
            ))}
        </div>
    );
}
